import { makeAutoObservable, runInAction } from 'mobx';
import { TwoFactorAuthService } from '@services';
import type {
  TwoFactorStatusDto,
  TrustedDeviceDto,
  SendTwoFactorCodeInput,
  SendPhoneVerificationCodeInput,
  VerifyPhoneInput,
  VerifyTwoFactorEnableInput,
  TotpSetupDto,
} from '@services';

class TwoFactorStore {
  // ─── Observable State ──────────────────────────────────────────────────────
  status: TwoFactorStatusDto | undefined = undefined;
  /** Challenge token issued by sendEnableVerificationCode / sendDisableVerificationCode. */
  pendingChallengeToken: string | undefined = undefined;
  /** TOTP setup payload returned by GenerateTotpSetup. Cleared after success or cancel. */
  totpSetup: TotpSetupDto | null = null;
  loading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── Computed ──────────────────────────────────────────────────────────────

  get isEnabled(): boolean {
    return this.status?.isEnabled ?? false;
  }

  get trustedDevices(): TrustedDeviceDto[] {
    return this.status?.trustedDevices ?? [];
  }

  get method(): string | null {
    return this.status?.method ?? null;
  }

  /** The method that will be used when enabling 2FA (from confirmed phone/email). */
  get resolvedMethod(): string {
    return this.status?.resolvedMethod ?? 'Email';
  }

  get phoneNumber(): string | null {
    return this.status?.phoneNumber ?? null;
  }

  get isPhoneConfirmed(): boolean {
    return this.status?.isPhoneConfirmed ?? false;
  }

  // ─── Actions ───────────────────────────────────────────────────────────────

  async loadStatus() {
    this.loading = true;
    try {
      const result = await TwoFactorAuthService.getStatus();
      runInAction(() => {
        this.status = result;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async sendEnableCode(input: SendTwoFactorCodeInput) {
    this.loading = true;
    try {
      const result = await TwoFactorAuthService.sendEnableVerificationCode(input);
      runInAction(() => {
        this.pendingChallengeToken = result.challengeToken;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async verifyAndEnable(code: string) {
    if (!this.pendingChallengeToken) throw new Error('No pending challenge token.');
    const input: VerifyTwoFactorEnableInput = {
      challengeToken: this.pendingChallengeToken,
      code,
    };
    this.loading = true;
    try {
      await TwoFactorAuthService.verifyAndEnable(input);
      runInAction(() => {
        this.pendingChallengeToken = undefined;
      });
      await this.loadStatus();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async sendDisableCode() {
    this.loading = true;
    try {
      const result = await TwoFactorAuthService.sendDisableVerificationCode();
      runInAction(() => {
        this.pendingChallengeToken = result.challengeToken;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async verifyAndDisable(code: string) {
    if (!this.pendingChallengeToken) throw new Error('No pending challenge token.');
    const input: VerifyTwoFactorEnableInput = {
      challengeToken: this.pendingChallengeToken,
      code,
    };
    this.loading = true;
    try {
      await TwoFactorAuthService.verifyAndDisable(input);
      runInAction(() => {
        this.pendingChallengeToken = undefined;
      });
      await this.loadStatus();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async removeTrustedDevice(deviceId: string) {
    this.loading = true;
    try {
      await TwoFactorAuthService.removeTrustedDevice({ deviceId });
      await this.loadStatus();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async sendPhoneVerificationCode(input: SendPhoneVerificationCodeInput) {
    this.loading = true;
    try {
      const result = await TwoFactorAuthService.sendPhoneVerificationCode(input);
      runInAction(() => {
        this.pendingChallengeToken = result.challengeToken;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async verifyPhone(input: VerifyPhoneInput) {
    this.loading = true;
    try {
      await TwoFactorAuthService.verifyPhone(input);
      runInAction(() => {
        this.pendingChallengeToken = undefined;
      });
      await this.loadStatus();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  resetPendingChallenge() {
    this.pendingChallengeToken = undefined;
  }

  async loadTotpSetup() {
    this.loading = true;
    try {
      const result = await TwoFactorAuthService.getTotpSetup();
      runInAction(() => {
        this.totpSetup = result;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async verifyAndEnableTotp(code: string) {
    if (!this.totpSetup) throw new Error('No pending TOTP setup.');
    this.loading = true;
    try {
      await TwoFactorAuthService.verifyAndEnableTotp({
        setupToken: this.totpSetup.setupToken,
        code,
      });
      runInAction(() => {
        this.totpSetup = null;
      });
      await this.loadStatus();
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  clearTotpSetup() {
    this.totpSetup = null;
  }
}

export default TwoFactorStore;
